import React, { useEffect, useState } from "react";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import AppDropdown from "../../components/AppDropdown/AppDropdown";
import * as apicall from "../../components/Apicall";
import { StatusPill } from "./LeadPills";
import { STATUS_META, canonicalStatus } from "./leadMeta";
import LeadDatePicker from "./LeadDatePicker";
import LeadVehiclePanel from "../Dms/leads/LeadVehiclePanel";

// ezp_Disposition's exact column names aren't settled yet — read whichever
// label field is actually present rather than assuming one.
const dispositionLabel = (d) => d.name ?? d.label ?? d.value ?? d.disposition_name ?? "";

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })
    : "";

const mapFollowup = (f) => ({
  id: f.id,
  kind: f.kind,
  who: f.kind === "system" ? "System" : f.kind === "auto" ? `${f.channel || "Automated"} nudge` : "Follow-up logged",
  when: formatDateTime(f.created_at),
  msg: f.message,
  disposition: f.disposition || undefined,
  deliveryStatus: f.delivery_status || undefined,
});

const WhatsAppIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
  </svg>
);
const NoteIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9"></path><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"></path>
  </svg>
);
const SystemIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4"></circle><path d="M4 21c0-4 4-6 8-6s8 2 8 6"></path>
  </svg>
);

const tlIcon = (kind) => {
  if (kind === "auto") return { bg: "#DCFCE7", color: "#16A34A", icon: <WhatsAppIcon /> };
  if (kind === "manual") return { bg: "#FFF1F4", color: "#C9194A", icon: <NoteIcon /> };
  return { bg: "#EEF2FF", color: "#4F46E5", icon: <SystemIcon /> };
};

const infoItem = (k, v) => (
  <div>
    <div style={{ fontSize: 11, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.04em", fontFamily: "'Inter', sans-serif", fontWeight: 600, marginBottom: 3 }}>{k}</div>
    <div style={{ fontSize: 13, color: "#111827", fontWeight: 500 }}>{v}</div>
  </div>
);

const fieldLabel = { display: "block", fontFamily: "'Inter', sans-serif", fontSize: 11.5, fontWeight: 600, color: "#374151", marginBottom: 5 };
const smallControl = { height: 36, fontSize: 12.5 };

// access = { userId, role, isSupportTeam, canManageLeads } from GET
// leads/my-access (see LeadsList.jsx). canManageLeads is true only for the
// vendor owner — the only role allowed to reassign a lead. Anyone else
// (including Support-team employees — isSupportTeam is display-only, no
// longer a permission bypass) can only act on a lead already assigned to
// them, and can never reassign it — same "visible to all, action gated by
// assignment" model as the WhatsApp chat screen.
const LeadDetailDrawer = ({ show, onHide, lead, onSaveUpdate, access, teamMembers = [] }) => {
  const [disposition, setDisposition] = useState("");
  const [status, setStatus] = useState("OPEN");
  const [nextFollowUp, setNextFollowUp] = useState("");
  const [note, setNote] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  // "Will they book?" — anchors the WhatsApp win-back cadence (day 3/7/20)
  // once set to NOT_BOOKING; also gates the 30min/6hr/24hr reminder cadence
  // when set to WILL_BOOK. Not required on every save — omitted from the
  // payload entirely unless the agent actually picks one.
  const [bookingIntent, setBookingIntent] = useState("");
  // Channels-sourced leads are created with only customer/source/reason (no
  // booking to copy details from), so these four are filled in here instead —
  // all optional (a chat lead may be a plain query with no booking), hidden
  // for every other lead.
  const [bookingId, setBookingId] = useState("");
  const [serviceName, setServiceName] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [serviceOffering, setServiceOffering] = useState("");
  const [dispositions, setDispositions] = useState([]);
  const [timeline, setTimeline] = useState([]);

  useEffect(() => {
    if (!show) return;
    apicall.get("ezp_disposition")
      .then((res) => setDispositions(res?.data || []))
      .catch(() => setDispositions([]));
  }, [show]);

  useEffect(() => {
    if (lead) {
      // Start on the lead's current disposition, like Status, so the dropdown shows what is already recorded.
      setDisposition(lead.lastDisposition || "");
      setStatus(canonicalStatus(lead.status));
      setNextFollowUp(lead.nextFollowUpDate || "");
      setNote("");
      setAssignedTo(lead.assignedAgentId != null ? String(lead.assignedAgentId) : "");
      setBookingIntent("");
      setBookingId(lead.bookingId != null ? String(lead.bookingId) : "");
      setServiceName(lead.service && lead.service !== "—" ? lead.service : "");
      setServiceType(lead.serviceType || "");
      setServiceOffering(lead.serviceOffering || "");
      setTimeline([]);
    }
  }, [lead]);

  useEffect(() => {
    if (!show || !lead) return;
    apicall.get(`leads/${lead.id}`)
      .then((res) => setTimeline((res?.data?.followups || []).map(mapFollowup)))
      .catch(() => setTimeline([]));
  }, [show, lead]);

  if (!lead) return null;

  const canReassign = !!access?.canManageLeads;
  const isAssignee = access?.userId != null && String(lead.assignedAgentId) === String(access.userId);
  const canManage = canReassign || isAssignee;
  const isChannelLead = lead.channel === "Channels";

  const handleSave = () => {
    const assignedAgentId = String(lead.assignedAgentId || "") !== assignedTo ? (assignedTo || null) : undefined;
    onSaveUpdate(lead.id, {
      ...(isChannelLead
        ? {
            bookingId: bookingId.trim(),
            serviceName: serviceName.trim(),
            serviceType: serviceType.trim(),
            serviceOffering: serviceOffering.trim(),
          }
        : {}),
      // Only a disposition the agent actually changed is sent: re-sending the current one would log it again,
      // count another attempt and restart its WhatsApp message schedule.
      disposition: disposition === (lead.lastDisposition || "") ? "" : disposition,
      status,
      nextFollowUp,
      note,
      ...(canReassign ? { assignedAgentId } : {}),
      ...(bookingIntent ? { bookingIntent } : {}),
    });
    setNote("");
    setBookingIntent("");
  };

  // One-click self-claim for a team member — fired directly rather than
  // routed through the "Save Update" button below, since that button stays
  // disabled (!canManage) for exactly the state this exists to fix: a lead
  // not yet assigned to this team member. Once it saves, the lead re-renders
  // with isAssignee=true and the rest of the form becomes editable normally.
  const handleAssignToMe = () => {
    if (access?.userId == null) return;
    onSaveUpdate(lead.id, { assignedAgentId: access.userId });
  };

  return (
    <SlidePanel
      show={show}
      onHide={onHide}
      title={lead.ref}
      width={620}
      footer={
        <div style={{ width: "100%" }}>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#C9194A", marginBottom: 10 }}>
            Log follow-up
          </div>
          {!canManage && (
            <div style={{ fontSize: 12, color: "#B45309", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
              You're not assigned to this lead — you can view it, but only the assigned team member or vendor owner can update it.
            </div>
          )}
          <div className="row g-2 mb-2">
            <div className="col-4">
              <label style={fieldLabel}>Disposition</label>
              <AppDropdown
                value={disposition}
                onChange={(e) => setDisposition(e.value)}
                options={dispositions.map((d) => ({ label: dispositionLabel(d), value: dispositionLabel(d) }))}
                placeholder="Select disposition…"
                disabled={!canManage}
                style={smallControl}
              />
            </div>
            <div className="col-4">
              <label style={fieldLabel}>Status</label>
              <AppDropdown
                value={status}
                onChange={(e) => setStatus(e.value)}
                options={Object.keys(STATUS_META).map((s) => ({ label: STATUS_META[s].label, value: s }))}
                disabled={!canManage}
                style={smallControl}
              />
            </div>
            <div className="col-4">
              <label style={fieldLabel}>Next follow-up</label>
              <LeadDatePicker value={nextFollowUp} onChange={setNextFollowUp} disabled={!canManage} />
            </div>
          </div>
          {/* <div className="mb-2">
            <label style={fieldLabel}>Will they book?</label>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn"
                onClick={() => setBookingIntent(bookingIntent === "WILL_BOOK" ? "" : "WILL_BOOK")}
                disabled={!canManage}
                style={{
                  height: 34, borderRadius: 8, padding: "0 14px", fontSize: 12.5, fontWeight: 600,
                  border: "1px solid " + (bookingIntent === "WILL_BOOK" ? "#10B981" : "#E5E7EB"),
                  background: bookingIntent === "WILL_BOOK" ? "#ECFDF5" : "#fff",
                  color: bookingIntent === "WILL_BOOK" ? "#10B981" : "#6B7280",
                  opacity: canManage ? 1 : 0.5,
                }}
              >
                Yes, will book
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => setBookingIntent(bookingIntent === "NOT_BOOKING" ? "" : "NOT_BOOKING")}
                disabled={!canManage}
                style={{
                  height: 34, borderRadius: 8, padding: "0 14px", fontSize: 12.5, fontWeight: 600,
                  border: "1px solid " + (bookingIntent === "NOT_BOOKING" ? "#C9194A" : "#E5E7EB"),
                  background: bookingIntent === "NOT_BOOKING" ? "#FFF1F4" : "#fff",
                  color: bookingIntent === "NOT_BOOKING" ? "#C9194A" : "#6B7280",
                  opacity: canManage ? 1 : 0.5,
                }}
              >
                No, not booking
              </button>
            </div>
          </div> */}
          <div className="d-flex gap-2 align-items-end">
            <div style={{ flex: 1 }}>
              <label style={fieldLabel}>Note</label>
              <input className="form-control" style={smallControl} placeholder="Add a quick note…" value={note} onChange={(e) => setNote(e.target.value)} disabled={!canManage} />
            </div>
            <button
              className="btn btn-danger"
              style={{ height: 36, borderRadius: 8, padding: "0 18px", fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap", opacity: canManage ? 1 : 0.5 }}
              onClick={handleSave}
              disabled={!canManage}
            >
               Update
            </button>
          </div>
        </div>
      }
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <StatusPill status={status} large />
        <span style={{ fontSize: 12.5, color: "#9CA3AF" }}>Created {lead.createdAt}</span>
      </div>

      <div style={{ background: "#F9FAFB", border: "1px solid #F3F4F6", borderRadius: 10, padding: "14px 16px", margin: "16px 0 20px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 20px" }}>
          {infoItem("Customer", lead.customerName)}
          {infoItem("Phone", lead.phone)}
          {infoItem("Service", lead.service)}
          {infoItem("Cart value", lead.cartValue)}
          <div>
            <div style={{ fontSize: 11, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.04em", fontFamily: "'Inter', sans-serif", fontWeight: 600, marginBottom: 3 }}>Assigned to</div>
            {canReassign ? (
              <AppDropdown
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.value)}
                // value must be a string, same type as `assignedTo` (set via
                // String(lead.assignedAgentId) below) — PrimeReact's Dropdown
                // matches the selected option by strict-typed equality, so a
                // numeric m.id here against a stringified assignedTo would
                // never match and the field would always show its
                // placeholder ("Unassigned") even for a lead that IS
                // assigned, regardless of what handleSave actually sends.
                options={teamMembers.map((m) => ({ label: m.name || m.full_name, value: String(m.id) }))}
                placeholder="Unassigned"
                style={{ height: 30, fontSize: 12.5 }}
              />
            ) : isAssignee ? (
              <div style={{ fontSize: 13, color: "#111827", fontWeight: 500 }}>{lead.assignedAgent || "Unassigned"}</div>
            ) : (
              // No dropdown for a team member — self-claim only, never
              // reassign to someone else. One click, saves immediately.
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ fontSize: 13, color: "#111827", fontWeight: 500 }}>{lead.assignedAgent || "Unassigned"}</div>
                <button
                  type="button"
                  onClick={handleAssignToMe}
                  style={{ background: "none", border: "none", padding: 0, color: "#C9194A", fontSize: 12.5, fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                >
                  Assign to me
                </button>
              </div>
            )}
          </div>
          {infoItem("Disposition", (timeline.find((t) => t.disposition) || {}).disposition || lead.lastDisposition || "—")}
          {infoItem("Attempts so far", lead.attempts)}
          {lead.conversionChannel && infoItem("Conversion channel", lead.conversionChannel)}
        </div>
      </div>

      {isChannelLead && (
        <>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#C9194A", margin: "4px 0 12px" }}>
            Booking & service details
          </div>
          <div style={{ background: "#F9FAFB", border: "1px solid #F3F4F6", borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 20px" }}>
              {[
                ["Booking ID", bookingId, setBookingId, "e.g. 10234"],
                ["Service", serviceName, setServiceName, "e.g. Deep Cleaning"],
                ["Service type", serviceType, setServiceType, "e.g. Residential"],
                ["Service offering", serviceOffering, setServiceOffering, "e.g. 2 BHK Package"],
              ].map(([label, value, setter, placeholder]) => (
                <div key={label}>
                  <label style={fieldLabel}>{label}</label>
                  <input
                    className="form-control"
                    style={smallControl}
                    placeholder={placeholder}
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                    disabled={!canManage}
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {!isChannelLead && (lead.serviceType || lead.serviceOffering) && (
        <>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#6B7280", margin: "4px 0 12px" }}>
            Additional Information
          </div>
          <div style={{ background: "#F9FAFB", border: "1px solid #F3F4F6", borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 20px" }}>
              {lead.serviceType && infoItem("Service Type", lead.serviceType)}
              {lead.serviceOffering && infoItem("Service Offering", lead.serviceOffering)}
            </div>
          </div>
        </>
      )}

      <LeadVehiclePanel lead={lead} />

      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#6B7280", margin: "4px 0 12px" }}>
        Activity timeline
      </div>
      <div>
        {timeline.length === 0 && (
          <div style={{ fontSize: 12.5, color: "#9CA3AF", padding: "8px 0" }}>No activity yet.</div>
        )}
        {timeline.map((t, i) => {
          const ic = tlIcon(t.kind);
          const isLast = i === timeline.length - 1;
          return (
            <div key={t.id} style={{ display: "flex", gap: 12, paddingBottom: 18, position: "relative" }}>
              {!isLast && <div style={{ position: "absolute", left: 15, top: 34, bottom: 0, width: 1.5, background: "#EEF0F2" }} />}
              <div style={{ width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, zIndex: 1, background: ic.bg, color: ic.color }}>
                {ic.icon}
              </div>
              <div style={{ flex: 1, background: "#fff", border: "1px solid #F3F4F6", borderRadius: 10, padding: "10px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 12.5, color: "#111827" }}>{t.who}</span>
                  <span style={{ fontSize: 11, color: "#9CA3AF" }}>{t.when}</span>
                </div>
                <div style={{ fontSize: 12.5, color: "#4B5563", lineHeight: 1.5 }}>{t.msg}</div>
                {t.deliveryStatus && (
                  <span style={{
                    display: "inline-block", marginTop: 6, fontSize: 10.5, fontWeight: 600, padding: "1px 8px", borderRadius: 20, fontFamily: "'Inter', sans-serif",
                    background: t.deliveryStatus === "Read" ? "#ECFDF5" : "#EFF6FF",
                    color: t.deliveryStatus === "Read" ? "#065F46" : "#1D4ED8",
                  }}>
                    {t.deliveryStatus} · Auto
                  </span>
                )}
                {t.disposition && (
                  <span style={{ display: "inline-block", marginTop: 6, fontSize: 10.5, fontWeight: 600, padding: "1px 8px", borderRadius: 20, fontFamily: "'Inter', sans-serif", background: "#FFFBEB", color: "#B45309" }}>
                    Disposition: {t.disposition}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

    </SlidePanel>
  );
};

export default LeadDetailDrawer;
